import { Theme } from '@mui/material/styles';

/**
 * Default size configurations for CardInfo default variant
 */
export const getDefaultSizeConfig = (theme: Theme) => ({
  small: {
    maxWidth: '320px',
    padding: theme.spacing(6), // 48px
    iconContainer: theme.spacing(6), // 48px
    iconPadding: theme.spacing(2.5), // 20px
    iconSize: '20px',
    titleSize: '16px',
    descriptionSize: '14px',
  },
  medium: {
    maxWidth: '420px',
    padding: theme.spacing(9), // 72px
    iconContainer: theme.spacing(10), // 80px (increased from 64px)
    iconPadding: theme.spacing(5), // 40px (increased from 32px)
    iconSize: '28px',
    titleSize: '18px',
    descriptionSize: '16px',
  },
  large: {
    maxWidth: '520px',
    padding: theme.spacing(12), // 96px
    iconContainer: theme.spacing(10), // 80px
    iconPadding: theme.spacing(4.5), // 36px
    iconSize: '36px',
    titleSize: '20px',
    descriptionSize: '18px',
  },
  xl: {
    maxWidth: '640px',
    padding: theme.spacing(15), // 120px
    iconContainer: theme.spacing(12), // 96px
    iconPadding: theme.spacing(5.5), // 44px
    iconSize: '44px',
    titleSize: '24px',
    descriptionSize: '20px',
  },
  flexi: {
    maxWidth: 'max-content',
    padding: theme.spacing(9), // 72px
    iconContainer: theme.spacing(8), // 64px
    iconPadding: theme.spacing(3.5), // 28px
    iconSize: '28px',
    titleSize: '18px',
    descriptionSize: '16px',
  },
});

/**
 * Selectable styling configuration for CardInfo selectable variant
 * Updated with flexible widths for responsive layouts
 */
export const getSelectableStyleConfig = (theme: Theme) => ({
  card: {
    maxWidth: '354.5px',
    maxHeight: '84px',
    paddingLeft: theme.spacing(2), // 16px
    paddingTop: theme.spacing(2), // 12px
    borderRadius: theme.spacing(1.5), // 12px
    borderWidth: '1px',
  },
  mainLayout: {
    width: '100%',
    minHeight: '52px',
    gap: theme.spacing(2), // 16px
    spacing: 'md' as const,
    alignItems: 'flex-start' as const,
  },
  iconContainer: {
    width: '32px',
    height: '32px',
    borderRadius: theme.spacing(1.25), // 10px
    padding: theme.spacing(0.75), // 6px
    iconSize: '16px',
  },
  contentLayout: {
    width: '100%',
    minHeight: '52px',
    gap: theme.spacing(1), // 8px
    spacing: 'xs' as const,
    flex: 1,
  },
  titleRow: {
    width: '100%',
    height: '24px',
    gap: theme.spacing(1), // 8px
    spacing: 'xs' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
  },
  description: {
    width: '100%',
    minHeight: '20px',
    textAlign: 'left' as const,
  },
  colors: {
    background: (palette: any) => palette.M3?.white,
    border: (palette: any) => palette.M3?.sys?.light?.border,
    selectedBorder: (palette: any) => palette.M3?.sys?.light?.primary,
    iconContainerBackground: (palette: any) => palette.Primary?.['0'],
    iconContainerBorder: (palette: any) => palette.M3?.sys?.light?.primary,
    iconColor: (palette: any) => palette.Primary?.['300'],
    titleColor: (palette: any) => palette.Neutral?.['900'],
    descriptionColor: (palette: any) => palette.Neutral?.['500'],
  },
  cardContent: {
    padding: (theme: Theme) => theme.spacing(2), // 16px
  },
});

/**
 * Selectable No Radio styling configuration for CardInfo selectableNoRadio variant
 */
export const getSelectableNoRadioStyleConfig = (theme: Theme) => ({
  card: {
    maxWidth: '630px',
    maxHeight: '84px',
    paddingLeft: theme.spacing(2), // 16px
    paddingTop: theme.spacing(2), // 12px
    borderRadius: theme.spacing(1.5), // 12px
    borderWidth: '1px',
  },
  mainLayout: {
    width: '100%',
    minHeight: '52px',
    gap: theme.spacing(2), // 16px
    spacing: 'md' as const,
    alignItems: 'flex-start' as const,
  },
  iconContainer: {
    width: '32px',
    height: '32px',
    borderRadius: theme.spacing(1.25), // 10px
    padding: theme.spacing(0.75), // 6px
    iconSize: '16px',
  },
  contentLayout: {
    width: '100%',
    minHeight: '52px',
    gap: theme.spacing(1), // 8px
    spacing: 'xs' as const,
  },
  titleRow: {
    width: '100%',
    height: '24px',
    gap: theme.spacing(1), // 8px
    spacing: 'xs' as const,
    alignItems: 'center' as const,
    justifyContent: 'flex-start' as const,
  },
  description: {
    width: '100%',
    minHeight: '20px',
    textAlign: 'left' as const,
  },
  colors: {
    background: (palette: any) => palette.M3?.white,
    border: (palette: any) => palette.M3?.sys?.light?.primary,
    selectedBorder: (palette: any) => palette.M3?.sys?.light?.primary,
    iconContainerBackground: (palette: any) => palette.Primary?.['0'],
    iconContainerBorder: (palette: any) => palette.Primary?.['50'],
    iconColor: (palette: any) => palette.Primary?.['300'],
    titleColor: (palette: any) => palette.Neutral?.['900'],
    selectedTitleColor: (palette: any) =>
      palette.M3?.sys?.light?.['on-primary-container'],
    descriptionColor: (palette: any) => palette.Neutral?.['500'],
  },
  cardContent: {
    padding: (theme: Theme) => theme.spacing(2), // 16px
  },
});

/**
 * Selectable size configurations for CardInfo selectable variant
 */
export const getSelectableSizeConfig = (theme: Theme) => ({
  small: {
    cardMaxWidth: '280px',
    cardHeight: '68px',
    cardPadding: theme.spacing(1.5), // 12px
    cardBorderRadius: theme.spacing(1), // 8px
    mainLayoutWidth: '256px',
    mainLayoutMinHeight: '44px',
    mainLayoutGap: theme.spacing(1.5), // 12px
    iconContainerSize: '24px',
    iconContainerPadding: theme.spacing(0.5), // 4px
    iconContainerBorderRadius: theme.spacing(0.75), // 6px
    iconSize: '12px',
    contentLayoutWidth: '220px',
    contentLayoutMinHeight: '44px',
    contentLayoutGap: theme.spacing(0.75), // 6px
    titleRowWidth: '220px',
    titleRowHeight: '20px',
    titleRowGap: theme.spacing(0.75), // 6px
    descriptionWidth: '220px',
    descriptionMinHeight: '18px',
  },
  medium: {
    cardMaxWidth: '354.5px',
    cardHeight: '84px',
    cardPadding: theme.spacing(2), // 16px
    cardBorderRadius: theme.spacing(1.5), // 12px
    mainLayoutWidth: '322.5px',
    mainLayoutMinHeight: '52px',
    mainLayoutGap: theme.spacing(2), // 16px
    iconContainerSize: '32px',
    iconContainerPadding: theme.spacing(0.75), // 6px
    iconContainerBorderRadius: theme.spacing(1.25), // 10px
    iconSize: '16px',
    contentLayoutWidth: '274.5px',
    contentLayoutMinHeight: '52px',
    contentLayoutGap: theme.spacing(1), // 8px
    titleRowWidth: '274.5px',
    titleRowHeight: '24px',
    titleRowGap: theme.spacing(1), // 8px
    descriptionWidth: '274.5px',
    descriptionMinHeight: '20px',
  },
  large: {
    cardMaxWidth: '420px',
    cardHeight: '100px',
    cardPadding: theme.spacing(2.5), // 20px
    cardBorderRadius: theme.spacing(2), // 16px
    mainLayoutWidth: '375px',
    mainLayoutMinHeight: '60px',
    mainLayoutGap: theme.spacing(2.5), // 20px
    iconContainerSize: '40px',
    iconContainerPadding: theme.spacing(1), // 8px
    iconContainerBorderRadius: theme.spacing(1.5), // 12px
    iconSize: '20px',
    contentLayoutWidth: '327.5px',
    contentLayoutMinHeight: '60px',
    contentLayoutGap: theme.spacing(1.25), // 10px
    titleRowWidth: '327.5px',
    titleRowHeight: '28px',
    titleRowGap: theme.spacing(1.25), // 10px
    descriptionWidth: '327.5px',
    descriptionMinHeight: '22px',
  },
  xl: {
    cardMaxWidth: '500px',
    cardHeight: '120px',
    cardPadding: theme.spacing(3), // 24px
    cardBorderRadius: theme.spacing(2.5), // 20px
    mainLayoutWidth: '452px',
    mainLayoutMinHeight: '72px',
    mainLayoutGap: theme.spacing(3), // 24px
    iconContainerSize: '48px',
    iconContainerPadding: theme.spacing(1.25), // 10px
    iconContainerBorderRadius: theme.spacing(2), // 16px
    iconSize: '24px',
    contentLayoutWidth: '392px',
    contentLayoutMinHeight: '72px',
    contentLayoutGap: theme.spacing(1.5), // 12px
    titleRowWidth: '392px',
    titleRowHeight: '32px',
    titleRowGap: theme.spacing(1.5), // 12px
    descriptionWidth: '392px',
    descriptionMinHeight: '28px',
  },
});
